import { memo, useCallback, useMemo, useState, ChangeEvent } from 'react'
import DataTable, { IDataTableColumn } from 'react-data-table-component'
import { GS, GS_m } from 'back-end-api'
import { useTranslation } from 'services/Translation'
import LoaderMini from 'components/common/loaders/mini'

import texts from '../texts'
import {
  TimeFilterValue,
  buildTimeFilterParams,
  isCustomRangeValid,
  productLabel,
  formatCents,
  formatDuration,
  formatConsultDate,
} from '../helpers'
import { Panel, Notice, EmptyState, FilterBar, Field, Select, SecondaryButton } from '../Styles'

interface ConsultDetailsProps {
  filter: TimeFilterValue
}

const columns = (
  t: (key: string) => string,
  none: string
): IDataTableColumn<GS_m.AnalyticsConsultDetailsRow>[] => [
  {
    name: t('consultDate'),
    minWidth: '120px',
    cell: r => formatConsultDate(r.consult_date, none),
  },
  {
    name: t('product'),
    cell: r => productLabel(r.product, t),
    minWidth: '120px',
  },
  {
    name: t('duration'),
    center: true,
    // Empty (retained) for products other than MIA Complete (Requirement 8.5).
    cell: r => formatDuration(r.duration_minutes, none),
  },
  {
    name: t('gross'),
    right: true,
    cell: r => formatCents(r.gross_cents, r.currency),
  },
  {
    name: t('commission'),
    right: true,
    cell: r => formatCents(r.commission_cents, r.currency),
  },
  {
    name: t('net'),
    right: true,
    cell: r => formatCents(r.net_cents, r.currency),
  },
  {
    name: t('patientInvoice'),
    cell: r => r.patient_invoice_ref || none,
  },
  {
    name: t('viewConsult'),
    cell: r => r.view_consult_ref || none,
  },
]

// Task 15.3 — Consult Details tab (Requirements 8.1, 8.2, 8.7).
// Paginated per-consult rows with an optional product filter and a next-page
// control driven by next_page_token (page size is handled server-side).
const ConsultDetails = ({ filter }: ConsultDetailsProps) => {
  const { t } = useTranslation(texts)

  const [product, setProduct] = useState<string>('')
  // Stack of page tokens we've navigated through; enables a Previous control.
  // The current page token is the last entry ('' means the first page).
  const [tokenStack, setTokenStack] = useState<string[]>([''])

  const rangeValid = isCustomRangeValid(filter)
  const currentToken = tokenStack[tokenStack.length - 1]

  const params: GS_m.GetWalletConsultDetailsParams = useMemo(() => {
    const base = buildTimeFilterParams(filter)
    return {
      ...base,
      ...(product ? { product } : {}),
      ...(currentToken ? { page_token: currentToken } : {}),
    }
  }, [filter, product, currentToken])

  const { data, error, isLoading } = GS.useWalletConsultDetails(params, {
    enabled: rangeValid,
  })

  const rows = data?.rows ?? []
  const nextToken = data?.next_page_token

  const handleProduct = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
    setProduct(e.target.value)
    // Reset pagination when the filter changes.
    setTokenStack([''])
  }, [])

  const goNext = useCallback(() => {
    if (nextToken) {
      setTokenStack(prev => [...prev, nextToken])
    }
  }, [nextToken])

  const goPrev = useCallback(() => {
    setTokenStack(prev => (prev.length > 1 ? prev.slice(0, -1) : prev))
  }, [])

  const canGoPrev = tokenStack.length > 1
  const canGoNext = Boolean(nextToken)

  if (!rangeValid) {
    return (
      <Panel>
        <EmptyState>{t('invalidRange')}</EmptyState>
      </Panel>
    )
  }

  return (
    <Panel>
      <FilterBar>
        <Field>
          {t('productFilter')}
          <Select value={product} onChange={handleProduct} data-cy="consultProductFilter">
            <option value="">{t('allProducts')}</option>
            <option value="MIA Complete">{t('miaComplete')}</option>
            <option value="MIA Basic">{t('miaBasic')}</option>
          </Select>
        </Field>
      </FilterBar>

      {error ? (
        <Notice variant="error" data-cy="consultError">
          {t('errorLoading')}
        </Notice>
      ) : (
        <>
          <DataTable
            noHeader
            columns={columns(t, t('none'))}
            data={rows}
            progressPending={isLoading}
            progressComponent={<LoaderMini />}
            noDataComponent={<EmptyState>{t('empty')}</EmptyState>}
            dense
            highlightOnHover
          />

          <FilterBar style={{ marginTop: '1rem', marginBottom: 0 }}>
            <SecondaryButton
              type="button"
              onClick={goPrev}
              disabled={!canGoPrev}
              data-cy="consultPrev"
            >
              {t('previousPage')}
            </SecondaryButton>
            <SecondaryButton
              type="button"
              onClick={goNext}
              disabled={!canGoNext}
              data-cy="consultNext"
            >
              {t('nextPage')}
            </SecondaryButton>
          </FilterBar>
        </>
      )}
    </Panel>
  )
}

ConsultDetails.whyDidYouRender = true

export default memo(ConsultDetails)
